import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsUSInfluencer } from '../../hooks/useIsUSInfluencer';

// Route prefixes a FOREIGN influencer is allowed to access: the Zap flow (hub,
// automations, storefront, link-in-bio) plus account/settings and the
// subscription-confirmation page. Everything else — dashboard/campaigns,
// mycampaigns, brand-list, clanshop, campaign details, profile-view, etc. — is
// redirected to the Zap hub.
const ALLOWED_PREFIXES = [
    '/influencer/zap',
    '/influencer/storefront',
    '/influencer/link-in-bio',
    '/influencer/settings',
    // The new flow's Profile and its settings screens (it hides the India-only rows for them).
    '/influencer/profile',
    '/influencer/orders/confirmed',
];

const ZAP_HOME = '/influencer/zap';

const isAllowedPath = (pathname: string): boolean =>
    ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

/**
 * Restricts FOREIGN influencers to the Zap-only flow. The header/menu already
 * hides non-Zap items for them, but the menu alone can't stop a user from typing
 * a URL like /influencer/dashboard directly — this guard closes that bypass by
 * redirecting any non-Zap influencer route to the Zap hub. India users are never
 * affected (the check returns false → children render as-is).
 *
 * Foreignness comes from the reliable dialing_code (via useIsUSInfluencer), so
 * the check is synchronous — no API call and no flash of a blocked page.
 */
export const ZapOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { pathname } = useLocation();
    const isForeign = useIsUSInfluencer();

    // India / normal users are unrestricted.
    if (!isForeign) return <>{children}</>;

    // Foreign users: allow the Zap flow + settings; redirect everything else.
    if (isAllowedPath(pathname)) return <>{children}</>;

    return <Navigate to={ZAP_HOME} replace />;
};

export default ZapOnlyGuard;
